import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import { userState, appState } from '../../store';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { IoMoon, IoSunny, IoNotifications } from 'react-icons/io5';
import { AiFillExclamationCircle } from 'react-icons/ai';
import { Btn } from '../ds/Btn';
import { Logo } from '../ds/Logo';
import useApi from '../../hooks/useApi';
import { UserNotification } from '../../@backend';

const navLinks = [
  { to: '/',          label: 'Zápasy',   exact: true },
  { to: '/zebricek',  label: 'Žebříček', exact: false },
  { to: '/pravidla',  label: 'Pravidla', exact: false },
  { to: '/kontakt',   label: 'Kontakt',  exact: false },
];

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  <  1) return 'právě teď';
  if (mins  < 60) return `před ${mins} min`;
  if (hours < 24) return `před ${hours} h`;
  return `před ${days} d`;
}

export const Menu = () => {
  const userSnap = useSnapshot(userState);
  const navigate = useNavigate();
  const apiUrl   = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';
  const { backend } = useApi(userSnap.isLoggedIn);

  // ── dropdowns ────────────────────────────────────────────────────────────
  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [dropdownOpen,  setDropdownOpen]  = useState(false);
  const [notifOpen,     setNotifOpen]     = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifRef    = useRef<HTMLDivElement>(null);

  // ── notifications ────────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [notifLoading,  setNotifLoading]  = useState(false);

  const unreadCount = notifications.filter(n => !n.readAt).length;

  const fetchNotifications = useCallback(() => {
    if (!userSnap.isLoggedIn) return;
    setNotifLoading(true);
    backend.user.userControllerMyNotifications(30, 0)
      .then(setNotifications)
      .catch(() => {})
      .finally(() => setNotifLoading(false));
  }, [backend.user, userSnap.isLoggedIn]);

  // Initial fetch when logged in
  useEffect(() => {
    if (userSnap.isLoggedIn) fetchNotifications();
  }, [userSnap.isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh when notifications panel opens
  useEffect(() => {
    if (notifOpen) fetchNotifications();
  }, [notifOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close both dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markOneRead = (id: number) => {
    backend.user.userControllerMarkMyNotificationRead(id)
      .then(() => setNotifications(ns =>
        ns.map(n => n.id === id ? { ...n, readAt: new Date().toISOString() } : n)
      ))
      .catch(() => {});
  };

  const markAllRead = () => {
    backend.user.userControllerMarkAllMyNotificationsRead()
      .then(() => setNotifications(ns =>
        ns.map(n => ({ ...n, readAt: n.readAt ?? new Date().toISOString() }))
      ))
      .catch(() => {});
  };

  // ── auth / nav ────────────────────────────────────────────────────────────
  const logOut = () => {
    userState.isLoggedIn  = false;
    userState.token       = null;
    userState.userProfile = {} as any;
    setDropdownOpen(false);
    setNotifications([]);
    navigate('/');
  };

  const darkModeHandler = () => {
    userState.dark = !userSnap.dark;
    document.body.classList.toggle('dark');
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'relative text-[15px] font-semibold transition-colors duration-150 font-nunito',
      isActive
        ? 'text-white after:absolute after:bottom-[-4px] after:left-1/2 after:-translate-x-1/2 after:w-[18px] after:h-[3px] after:bg-white after:rounded-full'
        : 'text-white/80 hover:text-white',
    ].join(' ');

  const avatar = userSnap.userProfile?.avatar
    ? `${apiUrl}/avatars/${userSnap.userProfile.avatar}`
    : null;

  const initials = [
    userSnap.userProfile?.firstname?.charAt(0) ?? '',
    userSnap.userProfile?.lastname?.charAt(0)  ?? '',
  ].join('');

  return (
    <header className="bg-gradient-header sticky top-0 z-50 shadow-row">
      <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">

        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <Logo white height={32} />
        </Link>

        {/* Desktop nav links */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <NavLink key={link.to} to={link.to} end={link.exact} className={navLinkClass}>
              {link.label}
            </NavLink>
          ))}
          {userSnap.isLoggedIn && userSnap.userProfile?.role === 'Admin' && (
            <NavLink to="/admin" className={navLinkClass}>Admin</NavLink>
          )}
        </nav>

        {/* Right area */}
        <div className="flex items-center gap-1 flex-shrink-0">

          {/* Dark mode toggle */}
          <button
            onClick={darkModeHandler}
            title="Přepnutí režimu"
            className="w-9 h-9 flex items-center justify-center rounded-btn text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          >
            {userSnap.dark ? <IoSunny className="text-xl" /> : <IoMoon className="text-xl" />}
          </button>

          {/* ── Notifications bell (logged-in only) ── */}
          {userSnap.isLoggedIn && (
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => { setNotifOpen(v => !v); setDropdownOpen(false); }}
                title="Oznámení"
                className="relative w-9 h-9 flex items-center justify-center rounded-btn text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              >
                <IoNotifications className="text-xl" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-brand-orange text-white text-[10px] font-black font-nunito px-0.5 leading-none">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-bg-card rounded-content shadow-card border border-border-neutral overflow-hidden z-50">

                  {/* Panel header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border-neutral">
                    <span className="text-[14px] font-black text-text-primary font-nunito">
                      Oznámení
                      {unreadCount > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-brand-orange text-white text-[10px] font-black font-nunito px-1">
                          {unreadCount}
                        </span>
                      )}
                    </span>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-[12px] font-semibold text-brand-blue hover:text-brand-blue-dark font-nunito transition-colors"
                      >
                        Označit vše jako přečtené
                      </button>
                    )}
                  </div>

                  {/* Notification list */}
                  <div className="max-h-[380px] overflow-y-auto scrollbar-thin">
                    {notifLoading && notifications.length === 0 ? (
                      <div className="flex items-center justify-center py-10">
                        <div className="w-5 h-5 rounded-full border-2 border-brand-blue border-t-transparent animate-spin" />
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 gap-2">
                        <IoNotifications className="text-3xl text-text-muted/40" />
                        <p className="tb-caption">Žádná oznámení</p>
                      </div>
                    ) : (
                      notifications.map(n => (
                        <button
                          key={n.id}
                          onClick={() => { if (!n.readAt && n.id) markOneRead(n.id); }}
                          className={[
                            'w-full text-left px-4 py-3 border-b border-divider-row transition-colors',
                            'hover:bg-bg-app flex flex-col gap-0.5',
                            !n.readAt ? 'border-l-[3px] border-l-brand-blue pl-[13px] bg-brand-blue/5' : '',
                          ].join(' ')}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className={`text-[13px] font-bold font-nunito leading-snug ${!n.readAt ? 'text-text-primary' : 'text-text-muted'}`}>
                              {n.title}
                            </span>
                            <span className="text-[11px] text-text-muted font-nunito whitespace-nowrap flex-shrink-0 mt-0.5">
                              {timeAgo(n.createdAt)}
                            </span>
                          </div>
                          <p className="text-[12px] text-text-muted font-nunito leading-snug line-clamp-2">
                            {n.body}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Auth area ── */}
          {userSnap.isLoggedIn ? (
            /* User dropdown */
            <div className="relative ml-1" ref={dropdownRef}>
              <button
                onClick={() => { setDropdownOpen(v => !v); setNotifOpen(false); }}
                className="flex items-center gap-2 rounded-btn px-2 py-1 hover:bg-white/10 transition-colors"
              >
                {/* Status warnings */}
                {!userSnap.userProfile?.entryFeePaid && (
                  <AiFillExclamationCircle className="text-xl text-semantic-danger" title="Nezaplacený vstupní příspěvek" />
                )}
                {userSnap.userProfile?.bonusQuestionAnswer === null && (
                  <AiFillExclamationCircle className="text-xl text-brand-orange" title="Nevyplněná bonusová otázka" />
                )}
                {/* Name + points */}
                <div className="hidden sm:flex flex-col items-end leading-none gap-0.5">
                  <span className="text-[14px] font-semibold text-white font-nunito">
                    {userSnap.userProfile?.firstname}
                  </span>
                  <span className="text-[11px] font-bold text-white/70 font-nunito">
                    {userSnap.userProfile?.points ?? 0} bodů
                  </span>
                </div>
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full overflow-hidden bg-white/20 flex items-center justify-center flex-shrink-0">
                  {avatar ? (
                    <img src={avatar} alt="avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[13px] font-bold text-white font-nunito">{initials}</span>
                  )}
                </div>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-bg-card rounded-content shadow-card border border-border-neutral overflow-hidden z-50">
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-border-neutral">
                    <p className="text-[14px] font-bold text-text-primary font-nunito">
                      {userSnap.userProfile?.firstname} {userSnap.userProfile?.lastname}
                    </p>
                    <p className="text-[12px] text-text-muted font-nunito truncate">
                      {userSnap.userProfile?.email}
                    </p>
                    <p className="text-[12px] font-semibold text-brand-blue font-nunito mt-0.5">
                      {userSnap.userProfile?.points ?? 0} bodů
                    </p>
                  </div>

                  {/* Menu items */}
                  {[
                    { to: '/uzivatel/profil/muj-profil', label: 'Můj profil', warning: false },
                    {
                      to: '/uzivatel/poplatek',
                      label: 'Registrační poplatek',
                      warning: !userSnap.userProfile?.entryFeePaid,
                      warningColor: 'text-semantic-danger',
                    },
                    {
                      to: '/uzivatel/bonusova-otazka',
                      label: 'Bonusová otázka',
                      warning: userSnap.userProfile?.bonusQuestionAnswer === null,
                      warningColor: 'text-brand-orange',
                    },
                  ].map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center justify-between px-4 py-2.5 text-[14px] font-semibold text-text-primary hover:bg-bg-app transition-colors font-nunito"
                    >
                      {item.label}
                      {item.warning && (
                        <AiFillExclamationCircle className={`text-lg ${item.warningColor}`} />
                      )}
                    </Link>
                  ))}

                  {userSnap.userProfile?.role === 'Admin' && (
                    <Link
                      to="/admin"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-[14px] font-semibold text-text-primary hover:bg-bg-app transition-colors font-nunito"
                    >
                      Administrace
                    </Link>
                  )}

                  <div className="border-t border-border-neutral" />
                  <button
                    onClick={logOut}
                    className="w-full text-left px-4 py-2.5 text-[14px] font-semibold text-semantic-danger hover:bg-bg-app transition-colors font-nunito"
                  >
                    Odhlásit
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Login / Register */
            <div className="hidden sm:flex items-center gap-2 ml-2">
              <Btn
                variant="outline-white"
                size="sm"
                onClick={() => { appState.modals.showLoginModal = true; }}
              >
                Přihlásit
              </Btn>
              <Btn
                variant="white"
                size="sm"
                onClick={() => { appState.modals.showRegisterModal = true; }}
              >
                Registrovat
              </Btn>
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 w-9 h-9 items-center justify-center rounded-btn hover:bg-white/10 transition-colors ml-1"
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Otevřít menu"
          >
            <span className={`block w-5 h-0.5 bg-white rounded transition-transform duration-200 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-5 h-0.5 bg-white rounded transition-opacity duration-200 ${mobileOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-5 h-0.5 bg-white rounded transition-transform duration-200 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile slide-down menu */}
      {mobileOpen && (
        <div className="md:hidden bg-gradient-header border-t border-white/10 px-4 pb-4 flex flex-col gap-2">
          {navLinks.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.exact}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block py-2 text-[15px] font-semibold font-nunito ${isActive ? 'text-white' : 'text-white/75 hover:text-white'}`
              }
            >
              {link.label}
            </NavLink>
          ))}
          {userSnap.isLoggedIn && userSnap.userProfile?.role === 'Admin' && (
            <NavLink
              to="/admin"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block py-2 text-[15px] font-semibold font-nunito ${isActive ? 'text-white' : 'text-white/75 hover:text-white'}`
              }
            >
              Admin
            </NavLink>
          )}
          {!userSnap.isLoggedIn && (
            <div className="flex gap-2 mt-2">
              <Btn
                variant="outline-white"
                size="sm"
                fullWidth
                onClick={() => { appState.modals.showLoginModal = true; setMobileOpen(false); }}
              >
                Přihlásit
              </Btn>
              <Btn
                variant="white"
                size="sm"
                fullWidth
                onClick={() => { appState.modals.showRegisterModal = true; setMobileOpen(false); }}
              >
                Registrovat
              </Btn>
            </div>
          )}
        </div>
      )}
    </header>
  );
};
